import { Text } from '@vkontakte/vkui';
import React from 'react';
import { IS_PAID_USER } from 'reactApp/appHelpers/configHelpers';
import { renderComfirmationDialog as renderConfirmationDialog } from 'reactApp/components/BaseConfirmDialog/BaseConfirmDialog.helpers';
import { EButtonType } from 'reactApp/components/BaseConfirmDialog/BaseConformationDialog.types';
import { moveToSafeRequest, trashbinSafeSetRestoreButtonVisible } from 'reactApp/modules/trashbin/trashbin.module';
import { getGroupType } from 'reactApp/modules/userQuotaCleaner/helpers/getGroupType';
import { store as reduxStore } from 'reactApp/store';
import { type UserQuotaGroupId, UserQuotaGroupType } from 'reactApp/types/QuotaCleaner';
import { sendDwh } from 'reactApp/utils/ga';
import { getPlatformInfo } from 'reactApp/utils/gaHelpers';

export const enum SafeSource {
    TRASHBIN = 'trashbin',
    CLEANER = 'cleaner',
}

const sendSafeConfirmationDialogDwh = ({
    buttonName,
    action,
    groupId,
    place,
}: {
    action: 'show' | 'click' | 'close';
    buttonName?: 'confirm' | 'cancel';
    groupId?: UserQuotaGroupId;
    place: SafeSource;
}) => {
    const isQuotaCleaner = place === SafeSource.CLEANER;

    const dwhData = {
        name: 'items_to_safe',
        paid_user: IS_PAID_USER,
        platform: getPlatformInfo().join('_'),
        group_id: isQuotaCleaner ? groupId : undefined,
        group_type: isQuotaCleaner ? getGroupType(groupId) : undefined,
        button_name: buttonName,
        place,
    };

    const optionalButtonField: (keyof typeof dwhData)[] = buttonName ? ['button_name'] : [];
    const dwh_enabled_fields: (keyof typeof dwhData)[][] = isQuotaCleaner
        ? [['name', 'place', ...optionalButtonField, 'group_id', 'group_type', 'platform']]
        : [['name', 'place', ...optionalButtonField, 'platform']];

    sendDwh({
        eventCategory: 'dialog',
        action,
        dwh_enabled: true,
        dwh_enabled_fields,
        dwhData,
    });
};

interface ClearData {
    ids: string[];
    itemIds: string[];
    groupType: UserQuotaGroupType;
    groupId: UserQuotaGroupId;
    size: number;
}

type MoveToSafeArgs = { place: SafeSource.TRASHBIN; size: number } | { place: SafeSource.CLEANER; clearData: ClearData };

const getContent = (args: MoveToSafeArgs) => {
    if (args.place === SafeSource.TRASHBIN && args.size) {
        return 'Вы действительно хотите удалить выбранные файлы в корзине и освободить место?';
    }

    if (args.place === SafeSource.TRASHBIN && !args.size) {
        return 'Вы действительно хотите удалить все файлы в корзине и освободить место?';
    }

    if (args.place === SafeSource.CLEANER && args.clearData?.groupType === UserQuotaGroupType.Letter) {
        return 'Место освободится в едином пространстве Почты и Облака — у вас будет 3 месяца, чтобы вернуть письма';
    }

    return 'Место освободится в едином пространстве Почты и Облака — у вас будет 3 месяца, чтобы вернуть файлы';
};

export const moveToSafe = (args: MoveToSafeArgs) => {
    const { place } = args;
    const isQuotaCleaner = place === SafeSource.CLEANER;
    const groupId = isQuotaCleaner ? args.clearData.groupId : undefined;

    renderConfirmationDialog({
        dataQAId: 'move-2safe-dialog',
        renderHeader: () => <>Освободить место?</>,
        successButtonText: 'Освободить',
        cancelButtonText: 'Не нужно',
        buttons: [EButtonType.accept, EButtonType.cancel],
        closeOnAction: true,
        smallButtons: true,
        renderContent: () => <Text style={{ fontSize: 18, lineHeight: '24px', width: '376px' }}>{getContent(args)}</Text>,
        onCancel: () => {
            sendSafeConfirmationDialogDwh({ action: 'click', buttonName: 'cancel', place: args.place, groupId });
        },
        onSuccess: () => {
            sendSafeConfirmationDialogDwh({ action: 'click', buttonName: 'confirm', place: args.place, groupId });
            reduxStore.dispatch(trashbinSafeSetRestoreButtonVisible(true));
            reduxStore.dispatch(moveToSafeRequest(isQuotaCleaner ? args.clearData : undefined));
        },
        onClose: () => {
            sendSafeConfirmationDialogDwh({ action: 'close', place, groupId });
        },
        onShow: () => {
            sendSafeConfirmationDialogDwh({ action: 'show', place, groupId });
        },
    });
};
